import React, { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Drawer, useDrawer, drawerBackdropId } from '@frontend/design-system';
import { usePanelStore, usePanelShallowStore } from './store';

const drawerZIndex = theme.zIndex.modals - 1;

export const PanelLayout: React.FC = React.memo(() => {
  const { updateAvailableWidth, hasTrayPanels, heightOffset, fullscreenPanelId } = usePanelShallowStore(
    'updateAvailableWidth',
    'hasTrayPanels',
    'heightOffset',
    'fullscreenPanelId'
  );

  const hasFullscreenPanel = !!fullscreenPanelId;
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const { modalProps } = useDrawer({
    open: hasTrayPanels && !hasFullscreenPanel,
    onOpenChange: (open) => {
      if (!open) {
        const trayIds = usePanelStore.getState().trayIds;
        const panelCloseFunctions = usePanelStore.getState().panelCloseFunctions;
        trayIds.forEach((id) => panelCloseFunctions[id]?.());
      }
    },
    outsidePress: (event) =>
      (event.target as HTMLElement).id === drawerBackdropId ||
      !!(event.target as HTMLElement).closest('#weave-top-bar'),
  });

  const { modalProps: fullscreenModalProps } = useDrawer({
    open: hasFullscreenPanel,
    onOpenChange: (open) => {
      if (!open) {
        usePanelStore.setState({ fullscreenPanelId: null });
      }
    },
    outsidePress: (event) =>
      (event.target as HTMLElement).id === drawerBackdropId ||
      !!(event.target as HTMLElement).closest('#weave-top-bar'),
  });

  const resizeRafId = useRef<number | null>(null);

  useEffect(() => {
    function onResize() {
      if (resizeRafId.current) {
        cancelAnimationFrame(resizeRafId.current);
      }
      resizeRafId.current = requestAnimationFrame(() => {
        updateAvailableWidth(window.innerWidth);
      });
    }

    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      if (resizeRafId.current) cancelAnimationFrame(resizeRafId.current);
    };
  }, [updateAvailableWidth]);

  return (
    <motion.div
      css={css`
        height: 100%;
      `}
    >
      <div
        id='panel-root'
        css={css`
          display: flex;
          height: 100%;
          isolation: isolate;
        `}
      />
      {/* Regular tray drawer */}
      <Drawer
        keepMounted
        additionalHeightOffset={isXSmall ? 72 + heightOffset : 0}
        css={[
          css`
            display: flex;
            padding: 0;
            max-width: 100%;
            ${hasFullscreenPanel ? 'display: none;' : ''}
          `,
          isXSmall
            ? css`
                width: 100%;
              `
            : css`
                width: auto;
              `,
        ]}
        {...modalProps}
        size={isXSmall ? 'large' : 'auto'}
        zIndex={drawerZIndex}
      >
        <motion.div
          css={css`
            display: flex;
            height: 100%;
          `}
          id='tray-root'
        />
      </Drawer>

      {/* Fullscreen drawer */}
      <Drawer
        keepMounted
        showBackdrop={false}
        rootId='app-container'
        css={css`
          display: flex;
          padding: 0;
          width: 100%;
          max-width: 100%;
        `}
        {...fullscreenModalProps}
        size='full'
        zIndex={drawerZIndex + 1}
      >
        <motion.div
          css={css`
            display: flex;
            height: 100%;
            width: 100%;
          `}
          id='fullscreen-root'
        />
      </Drawer>
    </motion.div>
  );
});

PanelLayout.displayName = 'PanelLayout';
